import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { IoSearchSharp } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import AdminPagination from "../../component/pagination/AdminPagination";
import SkeletonLoading from "../../component/SkeletonLoading";
import i18n from "../../i8next";
import { useGetRatingQuery } from "../../store/api/vendor/ratingSlice";
import { formatDateToLocal } from "../../utils/dateAndTimeConversion";

const initialActions = {
  rating: "all",
  search: "",
  role: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

const RatingStars = ({ rate }) => {
  const totalStars = 5;
  return (
    <span>
      {[...Array(totalStars)].map((_, i) => (
        <span key={i} style={{ color: i < rate ? "#ffc107" : "#e4e5e9" }}>
          ★
        </span>
      ))}
    </span>
  );
};

const Reviews = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [action, setAction] = useState(initialActions);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [ratingStar, setRatingStar] = useState("");

  const { slug } = useParams();
  // slug base fetch data
  const userRole =
    slug === "school-ratings"
      ? "school"
      : slug === "vendor-ratings"
        ? "vendor"
        : null;

  const { data, isSuccess, isLoading, isFetching, refetch } = useGetRatingQuery(
    {
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      rating: ratingStar,
      role: userRole,
    },
  );

  useEffect(() => {
    if (isSuccess && data?.data) {
      setReviews(data?.data?.ratings);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  // Handle search with debounce
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ ...prev, limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: debouncedSearch }));
    } else if (!search.trim()) {
      setPagination((prev) => ({ ...prev, limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [debouncedSearch, search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAction((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, limit: 10, page: 1 }));
  };

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({ ...prev, page: selectedItem.selected + 1 }));
  };

  const handlePageChange2 = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleChangeRating = (e) => {
    setRatingStar(e.target.value);
    setAction((prev) => ({ ...prev, rating: e.target.value }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setSearch("");
    setRatingStar("");
    setAction(initialActions);
    setPagination(initialPagination);
  };

  const isSuperAdmin = user?.role === "super_admin";

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  });

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="rating"
              value={ratingStar}
              onChange={handleChangeRating}
            >
              <option value="all">
                {t("select")} {t("rating")}
              </option>
              <option value="1">1 {t("star")}</option>
              <option value="2">2 {t("star")}</option>
              <option value="3">3 {t("star")}</option>
              <option value="4">4 {t("star")}</option>
              <option value="5">5 {t("star")}</option>
            </select>
          </div>

          <div className="d-flex gap-2 flex-grow-1">
            <button
              className="btn btn-primary addDriver flex-grow-1"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("SR")}.{t("no")}.
              </th>
              <th>{t("date")}</th>
              <th>{t("parent")}</th>
              <th className="text-nowrap">
                {t("vehicle")} {t("owner")}
              </th>
              <th>{t("driver")}</th>
              <th className="text-nowrap">
                {t("license")} {t("number")}
              </th>
              <th className="text-nowrap">
                {t("vehicle")} {t("type")}
              </th>
              <th>{t("rating")}</th>
              <th>{t("comment")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={9} />
            ) : reviews?.length > 0 ? (
              reviews.map((item, i) => (
                <tr key={item.id}>
                  <td> {(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>
                  <td className="text-nowrap">{item?.parent?.full_name}</td>
                  <td className="text-nowrap">
                    {item?.vehicle_owner?.full_name}
                  </td>
                  <td className="text-nowrap">{item?.driver?.full_name}</td>
                  <td className="text-nowrap">
                    {item?.vehicle?.license_number}
                  </td>
                  <td className="text-nowrap">{item?.vehicle?.vehicle_type}</td>
                  <td>
                    <RatingStars rate={item.rating} />
                  </td>
                  <td>
                    <NavLink className="text-break">{item.comment}</NavLink>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="12">
                  <div>
                    <span>
                      {t("no")} {t("record")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
    </>
  );
};

export default Reviews;
